import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Download, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { downloadStazionePdf, fetchStazioneStatus } from '../lib/stazionePdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StazioneSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [familyCode, setFamilyCode] = useState(null);
  const [caregiverUrl, setCaregiverUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  
  const sessionId = searchParams.get('session_id');
  const famigliaId = searchParams.get('famiglia_id');

  useEffect(() => {
    const completePayment = async () => {
      if (!sessionId || !famigliaId) {
        setError('Parametri mancanti');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${API_URL}/api/checkout/stazione-digitale/complete?session_id=${sessionId}&famiglia_id=${famigliaId}`,
          { method: 'POST' }
        );

        const data = await response.json();

        if (response.ok && data.success) {
          setFamilyCode(data.family_code);
          const status = await fetchStazioneStatus(famigliaId).catch(() => ({}));
          if (status.caregiver_url) setCaregiverUrl(status.caregiver_url);
        } else {
          setError(data.detail || 'Errore durante il completamento');
        }
      } catch (err) {
        console.error('Complete payment error:', err);
        setError('Errore di connessione');
      } finally {
        setLoading(false);
      }
    };

    completePayment();
  }, [sessionId, famigliaId]);

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      await downloadStazionePdf(famigliaId, familyCode);
      toast.success('PDF scaricato! Stampalo e appendilo in casa');
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error(err.message || 'Errore nella generazione del PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Attivazione in corso...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-xl max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">❌</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Errore</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button 
            onClick={() => navigate('/dashboard')}
            className="bg-gray-800 hover:bg-gray-900"
          >
            Torna alla Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-md w-full">
        {/* Success Icon */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Stazione Digitale Attivata!
          </h1>
          <p className="text-gray-600">
            Il tuo QR code universale è pronto
          </p>
        </div>

        {/* QR Code assistente */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 text-white mb-6">
          {caregiverUrl && (
            <div className="flex justify-center" data-testid="stazione-success-caregiver-qr">
              <div className="bg-white p-3 rounded-xl">
                <QRCodeSVG value={caregiverUrl} size={160} level="M" />
              </div>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="space-y-4 mb-6">
          <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-xl">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-purple-600 font-bold">1</span>
            </div>
            <div>
              <p className="font-medium text-gray-800">Scarica il PDF</p>
              <p className="text-sm text-gray-600">Stampa il QR code e appendilo in casa</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-xl">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-purple-600 font-bold">2</span>
            </div>
            <div>
              <p className="font-medium text-gray-800">Assistente scansiona il QR</p>
              <p className="text-sm text-gray-600">Inquadra il QR code o apre il link diretto: l'app si collega da sola</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-xl">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-purple-600 font-bold">3</span>
            </div>
            <div>
              <p className="font-medium text-gray-800">Ricevi notifiche push</p>
              <p className="text-sm text-gray-600">Ogni volta che l'assistente conferma un'attività</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            type="button"
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
            className="w-full flex items-center justify-center gap-2 border border-purple-200 text-purple-700 hover:bg-purple-50 rounded-md px-4 py-2 font-medium transition-colors disabled:opacity-60"
            data-testid="btn-success-download-pdf"
          >
            {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Scarica PDF con QR Code
          </button>
          
          <Button 
            onClick={() => navigate('/dashboard')}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
          >
            Vai alla Dashboard
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StazioneSuccessPage;
